import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Inject, Injectable, LOCALE_ID } from '@angular/core';
import { environment } from 'environments/environment';
import { LanguageModel } from 'models/generated/Language.Api.Models.Language';
import { LanguageVariableModel } from 'models/generated/Language.Api.Models.LanguageVariable';
import { LanguageVariableDtoModel } from 'models/generated/Language.Api.Models.LanguageVariableDto';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RelationService } from './relation.service';

@Injectable()
export class LanguageService  {

	httpOptions = {
		headers: new HttpHeaders({ 'Content-Type': 'application/json' })
	};

	constructor(private http: HttpClient, @Inject(LOCALE_ID) locale: string) {
		this.isoCode = locale.toLowerCase();
	}

	private language?: LanguageModel;
	private requestedVariables = new Map<string, LanguageVariableModel>();
	private requestedVariablesSubject = new Subject<LanguageVariableModel[]>();

	public onError = new EventEmitter<HttpErrorResponse>();
	public isoCode: string;
	public get ready() { return this.language !== undefined; }

	async resolve() {
		const cachedLanguage: LanguageModel | null = JSON.parse(localStorage.getItem(`gorm.language.${this.isoCode}`) ?? 'null');
		if (!cachedLanguage) {
			await this.loadLanguage();
			return;
		}
		RelationService.retrocycle(cachedLanguage);
		this.language = cachedLanguage;
		(new Promise(f => setTimeout(f, 3000))).then(_ => this.loadLanguage()); // Delay language request without awaiting it, to prevent blocking other load-blocking requests
	}

	private async loadLanguage() {
		try {
			this.language = await firstValueFrom(this.http.get<LanguageModel>(environment.gatewayUrl + '/api/languages/get/' + this.isoCode, this.httpOptions).pipe(tap()));
			this.updateCache();
			this.updateRequestedVariables();
		} catch (error) {
			if (error instanceof HttpErrorResponse) return this.onError.next(error);
		}
	}

	private updateCache() {
		if (this.language) localStorage.setItem(`gorm.language.${this.isoCode}`, JSON.stringify(RelationService.decycle(this.language)));
	}

	private updateRequestedVariables() {
		if (!this.language) return;
		for (const requestedVariable of this.requestedVariables.values()) {
			const existingVariable = this.language.languageVariables.find(v => v.identifier == requestedVariable.identifier);
			if (existingVariable) Object.assign(requestedVariable, existingVariable);
		}
	}

	getVariable(identifier: string, textVariables: any): LanguageVariableModel | undefined {
		let isNew = true;
		const variable = this.language ? this.language.languageVariables.find(variable => variable.identifier === identifier) : undefined;
		if (this.requestedVariables.get(identifier)) isNew = false;
		this.requestedVariables.set(identifier,
			variable
				? Object.assign(variable, { textVariables: textVariables })
				: Object.assign(new LanguageVariableModel(), { identifier: identifier, languageIsoCode: this.isoCode, textVariables: textVariables }));
		if (isNew) setTimeout(() => { this.requestedVariablesSubject.next(Array.from(this.requestedVariables.values())); });
		
		return variable;
	}

	currentLanguageVariables(): Observable<LanguageVariableModel[]> {
		return this.requestedVariablesSubject;
	}

	createLanguage(language: LanguageModel) {
		return firstValueFrom(this.http.post<LanguageModel>(environment.gatewayUrl + '/api/languages/create', language, this.httpOptions));
	}

	async saveLanguageVariable(newVariable: LanguageVariableModel) {
		const request = new LanguageVariableDtoModel();
		request.isoCode = this.isoCode;
		request.identifier = newVariable.identifier;
		request.text = newVariable.text;
		await firstValueFrom(this.http.post(environment.gatewayUrl + '/api/languages/set', request));

		if (!this.language) return;
		const existingVariable = this.language.languageVariables.find(v => v.identifier === newVariable.identifier);
		if (existingVariable)
			this.language.languageVariables.splice(this.language.languageVariables.indexOf(existingVariable), 1, newVariable);
		else
			this.language.languageVariables.push(newVariable);

		this.updateCache();
	}
}
